/**
 * ImportProgressPanel — read-only status surface for a single
 * `shop_history_import_runs` row.
 *
 * Used by both the preview page (during promote/analyze) and the
 * inferences page (as a small header). Polls every 2 seconds via
 * useImportRun; stops automatically once the run reaches a
 * terminal state.
 */

import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { ImportRunRow, ImportRunStatus } from '@/hooks/onboarding/useImportRun';

interface ImportProgressPanelProps {
  run: ImportRunRow | null;
  isLoading: boolean;
}

// Batch 8: user-facing copy. "Promote" stays in the internal
// promote-import-run Edge Function name; operators see "import" /
// "analyze" verbs only.
const STATUS_LABEL: Record<ImportRunStatus, string> = {
  staging: 'Uploading…',
  uploaded: 'Uploaded — ready to import',
  promoting: 'Importing historical rows…',
  promoted: 'Imported — ready to analyze',
  analyzing: 'Analyzing imported history…',
  analyzed: 'Analysis complete',
  failed: 'Import failed',
  discarded: 'Import discarded',
};

function StatusIcon({ status }: { status: ImportRunStatus }) {
  // Batch 8: the previous version returned a spinning loader for any
  // status that wasn't terminal — so `uploaded` (waiting for the user
  // to click Import & Analyze) and `promoted` (waiting to be analyzed)
  // both showed an active-work spinner that contradicted the text.
  // Idle/ready states now render a check; spinner is reserved for
  // genuinely active states.
  if (status === 'analyzed' || status === 'uploaded' || status === 'promoted') {
    return <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />;
  }
  if (status === 'failed') {
    return <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />;
  }
  return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />;
}

export function ImportProgressPanel({ run, isLoading }: ImportProgressPanelProps) {
  if (isLoading && !run) {
    return (
      <Card data-testid="import-progress-loading">
        <CardContent className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading import progress…
        </CardContent>
      </Card>
    );
  }

  if (!run) {
    return (
      <Card data-testid="import-progress-empty">
        <CardContent className="py-3 text-sm text-muted-foreground">
          No import run found.
        </CardContent>
      </Card>
    );
  }

  const isFailed = run.status === 'failed';
  return (
    <Card data-testid="import-progress" data-status={run.status}>
      <CardContent className="space-y-2 py-4">
        <div className="flex items-center gap-2">
          <StatusIcon status={run.status} />
          <div className="font-medium" data-testid="import-progress-status">
            {STATUS_LABEL[run.status]}
          </div>
        </div>
        {run.chunk_total > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="import-progress-chunks">
            Chunks: {run.chunk_promoted} of {run.chunk_total} promoted
          </div>
        )}
        {run.rows_total > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="import-progress-rows">
            Rows: {run.rows_promoted} of {run.rows_total} promoted
            {run.rows_invalid > 0 && (
              <span data-testid="import-progress-invalid"> · {run.rows_invalid} invalid</span>
            )}
            {run.rows_skipped_duplicate > 0 && (
              <span data-testid="import-progress-skipped"> · {run.rows_skipped_duplicate} duplicates</span>
            )}
          </div>
        )}
        {isFailed && run.error_message && (
          <div
            className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
            data-testid="import-progress-error"
          >
            {run.error_message}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
